import { useCallback, useEffect, useRef } from 'react';
import { useTheme } from 'styled-components';
import { isEmpty } from 'lodash/fp';
import { LocalizedText } from '@mews/framework';
import { TextKey, MessageThreadType } from '@mews-commander/enums';
import { Empty, isMobileOrTablet, Loader, TooltippedIconButton, WithBreakpointRange } from '@mews-commander/ui';
import {
    Container,
    debounce,
    getSpacingToken,
    IconName,
    Stack,
    Tab,
    Tabs,
    withBreakpointRange,
} from '@optimus-web/core';
import ThreadsPanelItem from '../containers/ThreadsPanelItem';
import { PanelCard, ThreadsBlockWrapper, UlElement } from './style';

const SCROLLED_TO_BOTTOM_OFFSET = 100;

interface ThreadsPanelComponentProps extends WithBreakpointRange {
    threads: $TSFixMe[]; // TODO: pt.instanceOf(Thread)
    currentThreadId?: string;
    sendScroll?: boolean;
    offset: number;
    onThreadSelected: $TSFixMeFunction;
    fetchThreads: $TSFixMeFunction;
    createThread: $TSFixMeFunction;
    setThreadsViewMode: $TSFixMeFunction;
    areThreadsLoading?: boolean;
    unreadEnterpriseThreadsCount?: number;
    unreadPrivateThreadsCount?: number;
    canReceiveCustomerMessages?: boolean | unknown;
    arePrivateMessageThreadsEnabled: boolean | unknown;
    viewMode: MessageThreadType;
}

const ThreadsPanelComponent = ({
    breakpointRange,
    threads,
    currentThreadId,
    sendScroll,
    offset,
    onThreadSelected,
    fetchThreads,
    createThread,
    setThreadsViewMode,
    areThreadsLoading,
    unreadEnterpriseThreadsCount,
    unreadPrivateThreadsCount,
    canReceiveCustomerMessages = false,
    arePrivateMessageThreadsEnabled,
    viewMode,
}: ThreadsPanelComponentProps) => {
    const theme = useTheme();
    const listRef = useRef<HTMLUListElement>(null);

    const handleListScroll = useCallback(() => {
        if (sendScroll && listRef.current) {
            const scrolledToBottom =
                listRef.current.scrollHeight - (listRef.current.scrollTop + listRef.current.clientHeight) <
                SCROLLED_TO_BOTTOM_OFFSET;
            if (scrolledToBottom) {
                fetchThreads({ threadType: viewMode, startIndex: offset });
            }
        }
    }, [sendScroll, offset, fetchThreads, viewMode]);

    useEffect(() => {
        const handler = debounce(handleListScroll, 500);
        const list = listRef.current;

        if (list) {
            list.addEventListener('scroll', handler);
        }

        return () => {
            if (list) {
                list.removeEventListener('scroll', handler);
            }
        };
    }, [sendScroll, offset, fetchThreads, viewMode, handleListScroll]);

    const handleThreadSelected = (thread: $TSFixMe) => {
        onThreadSelected({ threadId: thread.id });
    };

    const renderThreadItem = (thread: $TSFixMe) => {
        const props = {
            key: thread.id,
            thread,
            isActive: currentThreadId === thread.id,
            onThreadSelected: handleThreadSelected,
        };
        return <ThreadsPanelItem {...props} />;
    };

    const threadList = <UlElement ref={listRef}>{threads.map(renderThreadItem)}</UlElement>;

    const noThreads = (
        <Stack alignment="center" fill>
            {areThreadsLoading ? (
                <Loader />
            ) : (
                <Container padding={['200', '0']}>
                    <Empty />
                </Container>
            )}
        </Stack>
    );

    const handleTabSwitch = (_e: $TSFixMe, value: $TSFixMe) => setThreadsViewMode(value);

    const isPrivateViewMode = viewMode === MessageThreadType.Private;

    const padding = `${getSpacingToken('200')({ theme })}px`;

    const newThreadButton = isPrivateViewMode ? (
        <Container position="absolute" bottom={padding} right={padding}>
            {/* Note: Tooltip is not working with disabled button. */}
            <TooltippedIconButton
                onClick={createThread}
                variant="primary"
                icon={IconName.PlusSimple}
                disabled={!arePrivateMessageThreadsEnabled}
                tooltip={
                    !arePrivateMessageThreadsEnabled ? <LocalizedText textKey={TextKey.SubscriptionTierError} /> : null
                }
                type="submit"
            />
        </Container>
    ) : null;

    const customerTab = (
        <Tab
            id={TextKey.CustomerPlural}
            label={<LocalizedText textKey={TextKey.CustomerPlural} />}
            icon={IconName.CustomerProfiles}
            value={MessageThreadType.Enterprise}
            notificationsCount={unreadEnterpriseThreadsCount}
        />
    );
    const internalTab = (
        <Tab
            id={TextKey.Internal}
            label={<LocalizedText textKey={TextKey.Internal} />}
            icon={IconName.Property}
            value={MessageThreadType.Private}
            notificationsCount={unreadPrivateThreadsCount}
        />
    );

    const tabs = canReceiveCustomerMessages ? [customerTab, internalTab] : [internalTab];

    const isMobile = isMobileOrTablet(breakpointRange);

    return (
        <ThreadsBlockWrapper isSmall={isMobile}>
            <PanelCard padding="0">
                <Tabs activeTabValue={viewMode} onTabClick={handleTabSwitch}>
                    {tabs}
                </Tabs>
                {isEmpty(threads) ? noThreads : threadList}
                {newThreadButton}
            </PanelCard>
        </ThreadsBlockWrapper>
    );
};

export default withBreakpointRange(ThreadsPanelComponent);
